/**
 * emas — Exponential Moving Average computation for OHLCV bars.
 *
 * Pure math, no side effects. Two consumers:
 *
 *   1. The TradeChart, which renders one Lightweight Charts line series per
 *      requested period and overlays them on the candles.
 *
 *   2. The bar-analysis pipeline (maeMfe.js), which uses `emaAtTime()` to
 *      snapshot EMA values at the trade's entry timestamp so the Coach can
 *      reason about price-relative-to-EMA position.
 *
 * Algorithm: standard EMA with SMA seed.
 *   - First `period − 1` bars: returned as { value: null } (warm-up).
 *   - Bar at index `period − 1`: SMA of the first `period` closes.
 *   - Subsequent bars: EMA[i] = (close[i] − EMA[i−1]) × k + EMA[i−1]
 *     where k = 2 / (period + 1).
 *
 * Bars with NaN closes produce a null point and the running EMA pauses at
 * its prior value rather than poisoning the series with NaN propagation.
 *
 * Default periods (9/21/50/200) are a deliberate choice:
 *   - 9 EMA  — fast trigger / momentum
 *   - 21 EMA — Wilder-style intraday momentum
 *   - 50 EMA — session trend
 *   - 200 EMA — broader trend / "the line"
 * Custom periods are supported by passing an explicit array.
 */

export const DEFAULT_EMA_PERIODS = [5, 9, 21, 50, 200];

// Default colors per the user's preferred convention. Tuned for visibility
// on the chart's dark background while preserving the recognizable hue
// names. Override per-user via chartSettings.emaColors if desired — these
// are just the out-of-the-box defaults.
export const DEFAULT_EMA_COLORS = {
  5:   '#15803d',  // dark green   (Tailwind green-700)
  9:   '#00FF7F',  // spring green
  21:  '#FFFF00',  // yellow
  50:  '#FF0000',  // red
  200: '#FFFFFF',  // white
};

/**
 * Compute one EMA series.
 *
 * @param {Array<{time:number, close:number}>} bars  Sorted ascending by time.
 * @param {number} period  EMA period (positive integer, ≥ 1).
 * @returns {Array<{time:number, value:number|null}>}  Same length as `bars`.
 *          Points before the SMA seed (or with NaN closes) have value=null.
 */
export const computeEMA = (bars, period) => {
  if (!Array.isArray(bars) || bars.length === 0) return [];
  const p = Math.floor(Number(period));
  if (!Number.isFinite(p) || p < 1) return [];

  const k = 2 / (p + 1);
  const out = new Array(bars.length);
  let ema = null;
  let smaSum = 0;
  let validInSma = 0;

  for (let i = 0; i < bars.length; i++) {
    const t = Number(bars[i]?.time);
    const close = Number(bars[i]?.close);
    if (!Number.isFinite(close)) {
      // Pause EMA propagation through NaN bars; keep the running ema for the
      // next valid close. Output null so the chart shows a gap rather than a
      // misleading value.
      out[i] = { time: t, value: null };
      continue;
    }

    if (validInSma < p) {
      smaSum += close;
      validInSma++;
      if (validInSma === p) {
        ema = smaSum / p;
        out[i] = { time: t, value: ema };
      } else {
        out[i] = { time: t, value: null };
      }
      continue;
    }

    ema = (close - ema) * k + ema;
    out[i] = { time: t, value: ema };
  }

  return out;
};

/**
 * Compute all configured EMAs in one pass. Returns an object keyed by period:
 *   { 9: [...], 21: [...], 50: [...], 200: [...] }
 *
 * Periods array is deduped + sorted so callers can pass anything reasonable.
 */
export const computeEmas = (bars, periods = DEFAULT_EMA_PERIODS) => {
  const cleanedPeriods = Array.from(new Set(
    (periods || [])
      .map(p => Math.floor(Number(p)))
      .filter(p => Number.isFinite(p) && p >= 1)
  )).sort((a, b) => a - b);

  const out = {};
  for (const p of cleanedPeriods) {
    out[p] = computeEMA(bars, p);
  }
  return out;
};

/**
 * Look up the EMA value at (or just before) a given Unix-second timestamp.
 *
 * Used by the bar-analysis pipeline to snapshot "what was EMA9 at the moment
 * the trade entered?" without needing to recompute. Returns null when the
 * series is empty, all-null, or the target time precedes the first valid
 * point (i.e. inside the warm-up period).
 *
 * @param {Array<{time:number, value:number|null}>} series
 * @param {number} targetTime  Unix seconds.
 * @returns {number|null}
 */
export const emaAtTime = (series, targetTime) => {
  if (!Array.isArray(series) || series.length === 0) return null;
  if (!Number.isFinite(targetTime)) return null;

  let lastValue = null;
  for (const point of series) {
    if (!point) continue;
    if (point.time > targetTime) break;
    if (point.value !== null && Number.isFinite(point.value)) {
      lastValue = point.value;
    }
  }
  return lastValue;
};

/**
 * Snapshot all EMAs at a given timestamp into a flat object the Coach can read:
 *   { 9: 21450.25, 21: 21449.10, 50: 21442.75, 200: 21430.00 }
 *
 * Periods whose series hadn't warmed up by the target time return null.
 */
export const emaSnapshotAtTime = (allSeries, targetTime) => {
  if (!allSeries || typeof allSeries !== 'object') return null;
  const out = {};
  for (const [period, series] of Object.entries(allSeries)) {
    out[period] = emaAtTime(series, targetTime);
  }
  return out;
};
